import { useMemo } from 'react';
import { getColorWithAlpha } from '@elfsight/lib-utils';
import type { Appearance } from '@stripe/stripe-js';
import { useCheckoutColors } from '../providers';

export function usePaymentAppearances(): {
  appearance: Appearance;
  disabledAppearance: Appearance;
} {
  const { textColor, accentColor, errorColor, textColor50A, textColor20A } =
    useCheckoutColors();

  return useMemo(() => {
    const tabBorderColor = textColor20A;
    const hoverTabBorderColor = textColor50A;

    const inputLabelColor = textColor50A;
    const inputPlaceholderColor = getColorWithAlpha(textColor, 0.3);
    const inputBorderColor = textColor20A;

    const inputDisabledColor = textColor50A;
    const inputDisabledBackgroundColor = getColorWithAlpha(textColor, 0.05);
    const inputDisabledBorderColor = textColor20A;

    const actionHoverBackgroundColor = getColorWithAlpha(accentColor, 0.15);

    const menuActionBackgroundColor = getColorWithAlpha(textColor, 0.1);
    const menuActionHoverBackgroundColor = textColor20A;

    const appearance: Appearance = {
      labels: 'floating',
      // oxlint-disable-next-line
      // @ts-expect-error: https://docs.stripe.com/elements/appearance-api#inputs
      inputs: 'condensed',
      variables: {
        colorPrimary: textColor,
        colorText: textColor,
        colorDanger: errorColor,
        tabSpacing: '6px',
        gridColumnSpacing: '20px',
        gridRowSpacing: '20px',
        blockLogoColor: textColor,
        fontSizeBase: '16px',
        fontLineHeight: '22px'
      },
      rules: {
        '.Tab': {
          padding: '12px 20px',
          borderRadius: '8px',
          backgroundColor: 'transparent',
          border: `1px solid ${tabBorderColor}`,
          outline: 'none',
          color: textColor
        },

        '.Tab:hover': {
          border: `1px solid ${hoverTabBorderColor}`
        },

        '.Tab--selected, .Tab:focus, .Tab:active, .Tab--selected:focus': {
          border: `1px solid ${accentColor}`,
          outline: `1px solid ${accentColor}`,
          boxShadow: 'none'
        },

        '.TabLabel': {
          fontWeight: '500',
          fontSize: '15px',
          lineHeight: '20px'
        },

        '.TabIcon': {
          fill: textColor
        },

        '.Label, .Label--resting': {
          color: inputLabelColor
        },

        '.Label--floating': {
          fontSize: '11px',
          lineHeight: '12px'
        },

        '.Input': {
          padding: '8px 16px',
          fontSize: '16px',
          lineHeight: '22px'
        },

        '.Input, .PickerItem, .CheckboxInput': {
          padding: '16px',
          borderRadius: '8px',
          border: `1px solid ${inputBorderColor}`,
          outline: 'none',
          color: textColor,
          backgroundColor: 'transparent',
          fontSize: '16px',
          lineHeight: '22px'
        },

        '.Input--invalid': {
          color: textColor,
          border: `1px solid ${errorColor}`
        },

        '.Input--invalid:focus': {
          color: textColor,
          border: `1px solid ${errorColor}`,
          boxShadow: 'none'
        },

        '.Input:focus, .PickerItem--selected': {
          color: textColor,
          backgroundColor: 'transparent',
          border: `1px solid ${accentColor}`,
          boxShadow: 'none'
        },

        '.Input:disabled': {
          color: inputDisabledColor,
          backgroundColor: inputDisabledBackgroundColor,
          border: `1px solid ${inputDisabledBorderColor}`
        },

        '.Input::placeholder': {
          color: inputPlaceholderColor,
          fontSize: '16px'
        },

        '.Checkbox': {
          marginTop: '20px'
        },

        '.CheckboxInput': {
          borderRadius: '4px'
        },

        '.CheckboxInput:focus': {
          outline: 'none',
          border: `1px solid ${accentColor}`,
          boxShadow: 'none'
        },

        '.CheckboxInput--checked': {
          backgroundColor: accentColor
        },

        '.Error': {
          color: errorColor,
          fontSize: '13px',
          lineHeight: '16px',
          marginTop: '8px'
        },

        '.PickerItem:hover, .PickerItem--selected:hover': {
          backgroundColor: 'transparent',
          border: `1px solid ${inputBorderColor}`
        },

        '.Action': {
          color: accentColor,
          padding: '4px',
          border: 'none',
          fontWeight: '500',
          fontSize: '13px',
          lineHeight: '16px'
        },

        '.Action:hover': {
          backgroundColor: actionHoverBackgroundColor
        },

        '.Input .Action:hover': {
          backgroundColor: 'none'
        },

        '.Block': {
          padding: '12px',
          borderRadius: '8px',
          border: `1px solid ${inputBorderColor}`,
          outline: 'none',
          color: textColor,
          fill: textColor,
          backgroundColor: 'transparent'
        },

        '.BlockDivider': {
          backgroundColor: inputBorderColor
        },

        '.MenuAction, .MenuIcon': {
          color: textColor
        },

        '.MenuAction': {
          backgroundColor: menuActionBackgroundColor
        },

        '.MenuAction:hover, .MenuIcon:hover': {
          backgroundColor: menuActionHoverBackgroundColor
        }
      }
    };

    const disabledAppearance: Appearance = {
      ...appearance,
      rules: {
        ...appearance.rules,
        '.Input, .PickerItem, .CheckboxInput': {
          ...appearance?.rules?.['.Input, .PickerItem, .CheckboxInput'],
          ...appearance?.rules?.['.Input:disabled']
        }
      }
    };

    return { appearance, disabledAppearance };
  }, [textColor, accentColor, errorColor, textColor50A, textColor20A]);
}
